/**
 * LegalPage — Aviso de Privacidad y Términos del Servicio en rutas PÚBLICAS.
 *
 * Se monta FUERA del <Authenticator> (ver src/App.js): son documentos que deben poder
 * leerse sin iniciar sesión, tanto por obligación legal como porque TikTok exige que
 * ambos enlaces sean visibles en el sitio para aprobar la app (docs/tiktok_app_review.md).
 *
 * No usa el chasis de la app autenticada (sidebar, header, chat): es una página suelta,
 * imprimible, con su propio selector de idioma para que un revisor que llega en inglés
 * no tenga que pelearse con la UI.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n';
import { getLegalDoc, resolveLegalLang, LEGAL_ENTITY, LEGAL_LANGS } from './legalContent';
import defaultLogo from '../../assets/img/logo-data-new.png';
import './legal.css';

/** Cuerpo del documento, sin chrome. Se reutiliza dentro de la app autenticada
 *  (panel de administración) para no mantener dos textos legales distintos. */
export function LegalDocument({ doc }) {
  return (
    <>
      <h1 className="legal-title">{doc.title}</h1>
      <p className="legal-updated">{doc.updated}</p>
      <p className="legal-lead">{doc.lead}</p>
      {doc.sections.map((s) => (
        <section className="legal-section" key={s.title}>
          <h2>{s.title}</h2>
          {(s.paragraphs || []).map((p, i) => <p key={i}>{p}</p>)}
          {s.bullets && <ul>{s.bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>}
          {s.note && <p className="legal-note">{s.note}</p>}
        </section>
      ))}
    </>
  );
}

export default function LegalPage({ kind }) {
  const { language, setLanguage } = useLanguage();
  const doc = getLegalDoc(kind, language);
  // El documento puede caer a inglés aunque la UI esté en otro idioma (ver getLegalDoc):
  // los rótulos de navegación siguen al documento, no a la UI, para no mezclar idiomas.
  const isEs = resolveLegalLang(language) === 'es';
  const other = kind === 'privacy'
    ? { to: '/terms', label: isEs ? 'Términos del Servicio' : 'Terms of Service' }
    : { to: '/privacy', label: isEs ? 'Aviso de Privacidad' : 'Privacy Policy' };

  return (
    <div className="legal-page">
      <header className="legal-header">
        <img src={defaultLogo} alt={LEGAL_ENTITY} className="legal-logo"
             onError={(e) => { e.target.style.display = 'none'; }} />
        <div className="legal-langs">
          {LEGAL_LANGS.map((l) => (
            <button key={l.id} type="button"
              className={`legal-lang ${language === l.id ? 'active' : ''}`}
              onClick={() => setLanguage(l.id)}>{l.label}</button>
          ))}
        </div>
      </header>

      <main className="legal-main">
        <LegalDocument doc={doc} />
      </main>

      <footer className="legal-footer">
        <Link to={other.to}>{other.label}</Link>
        <span className="legal-sep">·</span>
        <Link to="/">{isEs ? 'Ir a la plataforma' : 'Go to the platform'}</Link>
        <span className="legal-sep">·</span>
        <span>© {new Date().getFullYear()} {LEGAL_ENTITY}</span>
      </footer>
    </div>
  );
}
